/**
 * API Service for PKA Ton Concours - Concours Management
 * Handles all CRUD operations for concours
 */
import axios, { AxiosInstance } from 'axios';
import {
  ConcoursCreateRequest,
  ConcoursDuplicateRequest,
  ConcoursListParams,
  ConcoursListResponse,
  ConcoursResponse,
  ConcoursStatsResponse,
  ConcoursUpdateRequest
} from '../types/concours';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

class ConcoursApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: `${API_BASE_URL}/api/pka`,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000,
    });

    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('concoursci_token');
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      return config;
    });
  }

  // ============================================
  // CRUD Operations
  // ============================================

  /**
   * List all concours with optional filters and pagination
   */
  async listConcours(params?: ConcoursListParams): Promise<ConcoursListResponse> {
    const response = await this.client.get('/concours', { params });
    return response.data;
  }

  /**
   * Get a single concours by ID
   */
  async getConcours(id: string): Promise<ConcoursResponse> {
    const response = await this.client.get(`/concours/${id}`);
    return response.data;
  }

  /**
   * Get a single concours by slug
   */
  async getConcoursBySlug(slug: string): Promise<ConcoursResponse> {
    const response = await this.client.get(`/concours/slug/${slug}`);
    return response.data;
  }

  /**
   * Create a new concours
   */
  async createConcours(data: ConcoursCreateRequest): Promise<ConcoursResponse> {
    const response = await this.client.post('/concours', data);
    return response.data;
  }

  /**
   * Update an existing concours
   */
  async updateConcours(id: string, data: ConcoursUpdateRequest): Promise<ConcoursResponse> {
    const response = await this.client.put(`/concours/${id}`, data);
    return response.data;
  }

  /**
   * Delete a concours
   */
  async deleteConcours(id: string, softDelete: boolean = false): Promise<{ success: boolean; message: string }> {
    const response = await this.client.delete(`/concours/${id}`, {
      params: { soft_delete: softDelete }
    });
    return response.data;
  }

  // ============================================
  // Bulk Operations
  // ============================================

  /**
   * Create multiple concours at once
   */
  async bulkCreateConcours(concours: ConcoursCreateRequest[]): Promise<{
    success: boolean;
    data: {
      created: number;
      failed: number;
      results: Array<{ id?: string; slug: string; success: boolean; error?: string }>;
    };
  }> {
    const response = await this.client.post('/concours/bulk', { concours });
    return response.data;
  }

  /**
   * Update multiple concours at once
   */
  async bulkUpdateConcours(
    ids: string[],
    updates: ConcoursUpdateRequest
  ): Promise<{
    success: boolean;
    data: { updated: number; failed: number };
  }> {
    const response = await this.client.patch('/concours/bulk', { ids, updates });
    return response.data;
  }

  // ============================================
  // Special Actions
  // ============================================

  /**
   * Duplicate a concours (useful for creating new editions)
   */
  async duplicateConcours(id: string, options: ConcoursDuplicateRequest): Promise<ConcoursResponse> {
    const response = await this.client.post(`/concours/${id}/duplicate`, options);
    return response.data;
  }

  /**
   * Publish a draft concours
   */
  async publishConcours(id: string): Promise<ConcoursResponse> {
    const response = await this.client.patch(`/concours/${id}/publish`);
    return response.data;
  }

  /**
   * Archive a completed concours
   */
  async archiveConcours(id: string): Promise<ConcoursResponse> {
    const response = await this.client.patch(`/concours/${id}/archive`);
    return response.data;
  }

  // ============================================
  // Statistics & Search
  // ============================================

  /**
   * Get concours statistics
   */
  async getStats(): Promise<ConcoursStatsResponse> {
    const response = await this.client.get('/concours/stats');
    return response.data;
  }

  /**
   * Search concours with full-text search
   */
  async searchConcours(
    query: string,
    fields?: string[],
    params?: ConcoursListParams
  ): Promise<ConcoursListResponse> {
    const response = await this.client.get('/concours/search', {
      params: {
        q: query,
        fields: fields?.join(','),
        ...params
      }
    });
    return response.data;
  }

  // ============================================
  // Utility Methods
  // ============================================

  /**
   * Generate a slug from a title
   */
  generateSlug(title: string, year?: number): string {
    let slug = title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric with hyphens
      .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens

    if (year) {
      slug += `-${year}`;
    }

    return slug;
  }

  /**
   * Validate concours data before submission
   */
  validateConcours(data: Partial<ConcoursCreateRequest>): string[] {
    const errors: string[] = [];

    if (!data.slug || !/^[a-z0-9-]+$/.test(data.slug)) {
      errors.push('Le slug est requis et doit contenir uniquement des lettres minuscules, chiffres et tirets');
    }
    if (!data.title || data.title.length < 5) {
      errors.push('Le titre est requis (minimum 5 caractères)');
    }
    if (!data.institution || data.institution.length < 3) {
      errors.push('L\'institution est requise (minimum 3 caractères)');
    }
    if (!data.year || data.year < 2024 || data.year > 2030) {
      errors.push('L\'année est requise (entre 2024 et 2030)');
    }
    if (!data.category) {
      errors.push('La catégorie est requise');
    }
    if (!data.level) {
      errors.push('Le niveau est requis');
    }

    return errors;
  }
}

export const concoursApiClient = new ConcoursApiClient();
export default concoursApiClient;
